import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HEADERS } from '../api-headers';

import {
  IServerSideGetRowsRequest,
  LoadSuccessParams,
} from 'ag-grid-community';

import {
  UTILS_LOG_GET_GET_OUTPUT_LOGS,
  UTILS_LOG_GET_GET_QUERY_HISTORY_INLINE,
  UTILS_LOG_GET_GET_OUTPUT_LOGS_DETAILS,
  UTILS_LOG_GET_GET_QUERY_HISTORY_LOGS,
  UTILS_LOG_GET_GET_SYNOPSIS_LOG,
  UTILS_LOG_GET_GET_USER_ACTION_LOG_EXTRA_METADATA,
} from '../endpoints/utils-log.endpoints';
import { toQueryParams } from '../api-utils';

import { TimeInfo } from 'src/app/core/models/core.models';

import { Observable } from 'rxjs';

export interface QueryHistoryRecord {
  saved_at: number; //unix timestamp
  query: string; // the query string
  saved_by: string; // display name of the user
}

export interface OutputLogs {
  totalCount: number;
  logs: OutputMetadata[];
}

export interface OutputMetadata {
  output_time: number;
  time: TimeInfo;
  output_errors: boolean;
  output_type: {
    id: number;
    name: string;
    icon_uri: string;
  };
  files: {
    name: string;
    base_name: string;
    language: string;
    url?: string;
    output_format: {
      id: number;
      name: string;
      icon_uri: string;
    };
  }[];
  object_class_name: string;
  object_type_name: string;
  object_name: string;
  company: {
    id: string | null;
    object_class_id: number | null;
    lifecycle_status: number | null;
    name: string;
  };
  user: {
    id: string;
    email: string;
    name: string;
    avatar_url: string;
    image_url: string;
  };
}

export interface UserActionLogExtraMetadataRequestParams {
  user_action_log_id: number | string;
}

export interface UserActionLogExtraMetadataResponse {
  extra_metadata?: {
    error?: {
      message?: string;
      trace?: string;
      [key: string]: unknown;
    };
    ext_log?: {
      record?: {
        HTTP_error_message?: string;
        cURL_error_message?: string;
        request_log_trace?: string;
        request_shards?: string[];
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class UtilsLogApiService {
  private readonly http = inject(HttpClient);

  getFeedHistory(
    objectId: string,
    objectClassId: number,
  ): Observable<Array<QueryHistoryRecord>> {
    const params = toQueryParams({
      object_id: objectId,
      object_class_id: objectClassId,
    });

    return this.http.get<Array<QueryHistoryRecord>>(
      UTILS_LOG_GET_GET_QUERY_HISTORY_INLINE,
      { params },
    );
  }

  getOutputLogs(
    objectId: string,
    objectClassId: number,
  ): Observable<OutputLogs> {
    const params = toQueryParams({
      object_id: objectId,
      object_class_id: objectClassId,
    });

    return this.http.get<OutputLogs>(UTILS_LOG_GET_GET_OUTPUT_LOGS, { params });
  }

  getUserSearchLogsAgGridModel(
    request: IServerSideGetRowsRequest,
    userId: string = null,
  ): Observable<LoadSuccessParams> {
    const filterMapping: { [key: string]: string } = {
      'company.name': 'fCompany',
      'user.name': 'fUser',
      'user.email': 'fEmail',
      'query.name': 'fObjectName',
      'query.type': 'fObjectType',
      'query.human_query': 'fSearchContext',
      action: 'fAction',
    };

    return this.http.get<LoadSuccessParams>(
      UTILS_LOG_GET_GET_QUERY_HISTORY_LOGS,
      {
        params: this.logsGridParams(request, filterMapping, userId),
        headers: HEADERS.formUrlEncoded,
      },
    );
  }

  fetchUserOutputLogsAgGridModel(
    request: IServerSideGetRowsRequest,
    userId: string = null,
  ): Observable<LoadSuccessParams> {
    const filterMapping: { [key: string]: string } = {
      'company.name': 'fCompany',
      'user.name': 'fUser',
      'user.email': 'fEmail',
      object_class_name: 'fObjectClass',
      object_type_name: 'fObjectType',
      object_name: 'fObjectName',
      action: 'fAction',
      output: 'fOutput',
    };

    return this.http.get<LoadSuccessParams>(
      UTILS_LOG_GET_GET_OUTPUT_LOGS_DETAILS,
      {
        params: this.logsGridParams(request, filterMapping, userId),
        headers: HEADERS.formUrlEncoded,
      },
    );
  }

  fetchSynopsisLogsAgGridModel(
    request: IServerSideGetRowsRequest,
  ): Observable<LoadSuccessParams> {
    const filterMapping: { [key: string]: string } = {
      'company.name': 'fCompany',
      'user.name': 'fUser',
      'user.email': 'fEmail',
      post_title: 'fPostTitle',
      synopsis_title: 'fSynopsisTitle',
      synopsis: 'fSynopsisContent',
    };

    return this.http.get<LoadSuccessParams>(UTILS_LOG_GET_GET_SYNOPSIS_LOG, {
      params: this.logsGridParams(request, filterMapping),
      headers: HEADERS.formUrlEncoded,
    });
  }

  /** Shared query-string shape of the three `/utils_log` agGrid readers. */
  private logsGridParams(
    request: IServerSideGetRowsRequest,
    filterMapping: { [key: string]: string },
    userId: string = null,
  ): HttpParams {
    // Hand-rolled, not toQueryParams: the wire keys are camelCase legacy
    // contract + dynamic filterMapping keys (api-layer.md, Edge cases).
    let httpParams = new HttpParams();

    // Default parameters
    httpParams = httpParams.set('output', 'agGrid');
    httpParams = httpParams.set('start', request.startRow?.toString() || '0');
    httpParams = httpParams.set(
      'itemsPerPage',
      ((request.endRow || 20) - (request.startRow || 0)).toString(),
    );

    if (userId) {
      httpParams = httpParams.set('userId', userId);
    }

    if (request.filterModel) {
      const filters = request.filterModel;

      // The date-range filter on the split absolute-time column maps to the
      // endpoints' unix startDate/endDate params. Seconds normalize to :00/:59
      // - live seconds would defeat the memcached layer.
      const dateRange = filters['time.absolute'];
      if (
        dateRange?.filterType === 'date' &&
        dateRange.dateFrom &&
        dateRange.dateTo
      ) {
        const from = new Date(String(dateRange.dateFrom).replace(' ', 'T'));
        const to = new Date(String(dateRange.dateTo).replace(' ', 'T'));
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
          from.setSeconds(0, 0);
          to.setSeconds(59, 999);
          httpParams = httpParams
            .set('startDate', Math.floor(from.getTime() / 1000).toString())
            .set('endDate', Math.floor(to.getTime() / 1000).toString());
        }
      }

      for (const key in filters) {
        if (filters.hasOwnProperty(key) && filterMapping[key]) {
          httpParams = httpParams.set(
            filterMapping[key],
            filters[key]?.filter || '',
          );
        }
      }
    }

    // Add sorting parameters if present
    if (request.sortModel && request.sortModel.length > 0) {
      const sortField = request.sortModel[0].colId;
      const sortDirection = request.sortModel[0].sort;
      httpParams = httpParams.set('sortField', sortField);
      httpParams = httpParams.set('sortDirection', sortDirection);
    }

    return httpParams;
  }

  getUserActionLogExtraMetadata(
    params: UserActionLogExtraMetadataRequestParams,
  ): Observable<UserActionLogExtraMetadataResponse> {
    const httpParams = toQueryParams({
      user_action_log_id: params.user_action_log_id,
    });

    return this.http.get<UserActionLogExtraMetadataResponse>(
      UTILS_LOG_GET_GET_USER_ACTION_LOG_EXTRA_METADATA,
      { params: httpParams },
    );
  }
}
